import { combineTransactionSteps, getChangedRanges, getMarksBetween, findChildrenInRange, posToDOMRect, getAttributes, Mark, mergeAttributes, markPasteRule } from '@tiptap/core';
import { test, find, registerCustomProtocol, reset } from 'linkifyjs';
import { Plugin, PluginKey } from '@tiptap/pm/state';
import tippy, { roundArrow } from 'tippy.js';

function autoHyperlink(options) {
    return new Plugin({
        key: new PluginKey("autoHyperlink"),
        appendTransaction: (transactions, oldState, newState) => {
            const docChanges = transactions.some((transaction) => transaction.docChanged) &&
                !oldState.doc.eq(newState.doc);
            const preventAutoHyperlink = transactions.some((transaction) => transaction.getMeta("preventAutoHyperlink"));
            if (!docChanges || preventAutoHyperlink) {
                return;
            }
            const { tr } = newState;
            const transform = combineTransactionSteps(oldState.doc, [
                ...transactions,
            ]);
            const { mapping } = transform;
            const changes = getChangedRanges(transform);
            changes.forEach(({ oldRange, newRange }) => {
                // at first we check if we have to remove links
                getMarksBetween(oldRange.from, oldRange.to, oldState.doc)
                    .filter((item) => item.mark.type === options.type)
                    .forEach((oldMark) => {
                    const newFrom = mapping.map(oldMark.from);
                    const newTo = mapping.map(oldMark.to);
                    const newMarks = getMarksBetween(newFrom, newTo, newState.doc).filter((item) => item.mark.type === options.type);
                    if (!newMarks.length) {
                        return;
                    }
                    const newMark = newMarks[0];
                    const oldLinkText = oldState.doc.textBetween(oldMark.from, oldMark.to, undefined, " ");
                    const newLinkText = newState.doc.textBetween(newMark.from, newMark.to, undefined, " ");
                    const wasLink = test(oldLinkText);
                    const isLink = test(newLinkText);
                    // remove only the link, if it was a link before too
                    // because we don’t want to remove links that were set manually
                    if (wasLink && !isLink) {
                        tr.removeMark(newMark.from, newMark.to, options.type);
                    }
                });
                // now let’s see if we can add new links
                const nodesInChangedRanges = findChildrenInRange(newState.doc, newRange, (node) => node.isTextblock);
                let textBlock;
                let textBeforeWhitespace;
                if (nodesInChangedRanges.length > 1) {
                    // Grab the first node within the changed ranges (ex. the first of two paragraphs when hitting enter)
                    textBlock = nodesInChangedRanges[0];
                    textBeforeWhitespace = newState.doc.textBetween(textBlock.pos, textBlock.pos + textBlock.node.nodeSize, undefined, " ");
                }
                else if (nodesInChangedRanges.length &&
                    // We want to make sure to include the block seperator argument to treat hard breaks like spaces
                    newState.doc
                        .textBetween(newRange.from, newRange.to, " ", " ")
                        .endsWith(" ")) {
                    textBlock = nodesInChangedRanges[0];
                    textBeforeWhitespace = newState.doc.textBetween(textBlock.pos, newRange.to, undefined, " ");
                }
                if (textBlock && textBeforeWhitespace) {
                    const wordsBeforeWhitespace = textBeforeWhitespace
                        .split(" ")
                        .filter((s) => s !== "");
                    if (wordsBeforeWhitespace.length <= 0) {
                        return false;
                    }
                    const lastWordBeforeSpace = wordsBeforeWhitespace[wordsBeforeWhitespace.length - 1];
                    const lastWordAndBlockOffset = textBlock.pos +
                        textBeforeWhitespace.lastIndexOf(lastWordBeforeSpace);
                    if (!lastWordBeforeSpace) {
                        return false;
                    }
                    find(lastWordBeforeSpace)
                        .filter((link) => link.isLink)
                        .filter((link) => {
                        if (options.validate) {
                            return options.validate(link.value);
                        }
                        return true;
                    })
                        // calculate link position
                        .map((link) => ({
                        ...link,
                        from: lastWordAndBlockOffset + link.start + 1,
                        to: lastWordAndBlockOffset + link.end + 1,
                    }))
                        // add link mark
                        .forEach((link) => {
                        tr.addMark(link.from, link.to, options.type.create({
                            href: link.href,
                        }));
                    });
                }
            });
            if (!tr.steps.length) {
                return;
            }
            return tr;
        },
    });
}

class Tooltip {
    constructor(options) {
        this.preventHide = false;
        this.mousedownHandler = () => {
            this.preventHide = true;
        };
        this.dragstartHandler = () => {
            this.hide();
        };
        this.blurHandler = ({ event }) => {
            var _a;
            if (this.preventHide) {
                this.preventHide = false;
                return;
            }
            if ((event === null || event === void 0 ? void 0 : event.relatedTarget) &&
                ((_a = this.tippyWrapper.parentNode) === null || _a === void 0 ? void 0 : _a.contains(event.relatedTarget))) {
                return;
            }
            this.hide();
        };
        this.tippyBlurHandler = (event) => {
            this.blurHandler({ event });
        };
        this.editor = options.editor;
        this.view = this.editor.view;
        this.tippyWrapper = document.createElement("div");
        this.tippyWrapper.addEventListener("mousedown", this.mousedownHandler, {
            capture: true,
        });
        this.view.dom.addEventListener("dragstart", this.dragstartHandler);
        this.editor.on("blur", this.blurHandler);
    }
    init() {
        this.tippyWrapper.innerHTML = "";
        return { tippyModal: this.tippyWrapper, tippyInstance: this.tippyInstance };
    }
    show() {
        var _a;
        (_a = this.tippyInstance) === null || _a === void 0 ? void 0 : _a.show();
        return true;
    }
    hide() {
        setTimeout(() => { var _a; return (_a = this.tippyInstance) === null || _a === void 0 ? void 0 : _a.hide(); });
        return false;
    }
    createTooltip() {
        if (!this.editor || !this.editor.options)
            return;
        const { element: editorElement } = this.editor.options;
        const editorIsAttached = !!editorElement.parentElement;
        if (this.tippyInstance || !editorIsAttached) {
            return;
        }
        this.tippyInstance = tippy(editorElement, {
            duration: 0,
            getReferenceClientRect: null,
            content: this.tippyWrapper,
            interactive: true,
            trigger: "manual",
            placement: "bottom",
            hideOnClick: true,
            onClickOutside: (instance, event) => {
                this.hide();
            },
            onAfterUpdate: (instance, partialProps) => {
                this.show();
            },
        });
        if (this.tippyInstance.popper.firstChild) {
            this.tippyInstance.popper.firstChild.addEventListener("blur", this.tippyBlurHandler);
        }
    }
    update(view, option = {}) {
        var _a;
        this.createTooltip();
        option.arrow = (_a = option === null || option === void 0 ? void 0 : option.arrow) !== null && _a !== void 0 ? _a : false;
        if (this.tippyInstance) {
            this.tippyInstance.setProps({
                ...option,
                getReferenceClientRect: () => {
                    const pos = view.state.selection.from;
                    // width: 0 is a hack to prevent tippy display in the wrong position
                    return { ...posToDOMRect(view, pos, pos), width: 0 };
                },
            });
        }
        return {};
    }
    destroyTooltip() {
        if (this.tippyInstance) {
            this.tippyInstance.destroy();
            this.tippyInstance = undefined;
            this.tippyWrapper.removeEventListener("mousedown", this.mousedownHandler, {
                capture: true,
            });
            this.view.dom.removeEventListener("dragstart", this.dragstartHandler);
            this.editor.off("blur", this.blurHandler);
        }
    }
}

function clickHandler(options) {
    // Create the tooltip instance
    let tooltip = new Tooltip(options);
    // Initialize the tooltip
    let { tippyModal, tippyInstance } = tooltip.init();
    return new Plugin({
        key: new PluginKey("handleClickHyperlink"),
        props: {
            handleClick: (view, pos, event) => {
                var _a, _b;
                if (event.button !== 0)
                    return false;
                // Get the target HTML element and its position
                const nodeTarget = event.target;
                const nodePos = view.posAtDOM(nodeTarget, 0);
                // Find the closest link element to the target element
                const link = nodeTarget === null || nodeTarget === void 0 ? void 0 : nodeTarget.closest("a");
                // Extract attributes from the state
                const attrs = getAttributes(view.state, options.type.name);
                // Extract href and target attributes from the link element or the state
                const href = (_a = link === null || link === void 0 ? void 0 : link.href) !== null && _a !== void 0 ? _a : attrs.href;
                const target = (_b = link === null || link === void 0 ? void 0 : link.target) !== null && _b !== void 0 ? _b : attrs.target;
                // If there is no previewHyperlink modal provided, then open the link in new window
                if (!options.modal) {
                    if (link && href) {
                        window.open(href, target);
                    }
                    return true;
                }
                // if the link does not contain href attribute, hide the tooltip
                if (!(link === null || link === void 0 ? void 0 : link.href))
                    return tooltip.hide();
                // Create a preview of the hyperlink
                const hyperlinkPreview = options.modal({
                    link,
                    nodePos,
                    tippy: tooltip,
                    ...options,
                });
                // If there is no hyperlink preview, hide the modal
                if (!hyperlinkPreview)
                    return tooltip.hide();
                // Empty the modal and append the hyperlink preview box
                while (tippyModal.firstChild) {
                    tippyModal.removeChild(tippyModal.firstChild);
                }
                tippyModal.append(hyperlinkPreview);
                // Update the modal position
                tooltip.update(options.editor.view);
                return false;
            },
        },
    });
}

function pasteHandler(options) {
    return new Plugin({
        key: new PluginKey("handlePasteHyperlink"),
        props: {
            handlePaste: (view, event, slice) => {
                const { state } = view;
                const { selection } = state;
                const { empty } = selection;
                if (empty) {
                    return false;
                }
                let textContent = "";
                slice.content.forEach((node) => {
                    textContent += node.textContent;
                });
                const link = find(textContent).find((item) => item.isLink && item.value === textContent);
                if (!textContent || !link) {
                    return false;
                }
                options.editor.commands.setMark(options.type, {
                    href: link.href,
                });
                return true;
            },
        },
    });
}

function editHyperlink(options) {
    var _a;
    const { state, dispatch } = options.editor.view;
    const { from, to } = state.selection;
    let link = null;
    const selectedNode = options.editor.view.domAtPos(from - 1).node;
    const nodeName = selectedNode === null || selectedNode === void 0 ? void 0 : selectedNode.nodeName;
    if (nodeName === "#text") {
        link = (_a = selectedNode.parentNode) === null || _a === void 0 ? void 0 : _a.closest("a");
    }
    else {
        link = selectedNode === null || selectedNode === void 0 ? void 0 : selectedNode.closest("a");
    }
    // If no link found and node is paragraph, try finding link in sibling/parent
    if (!link && options.editor.view.domAtPos(from).node.nodeName === "P" && selectedNode.nextSibling)
        link =
            selectedNode.nextSibling ||
                selectedNode.parentElement;
    if (!link)
        return true;
    const text = options.newText || (link === null || link === void 0 ? void 0 : link.innerText);
    // Get the position of the link in the editor view
    const nodePos = options.editor.view.posAtDOM(link, 0);
    const sanitizeURL = find(options.newURL || link.href)
        .filter((link) => link.isLink)
        .filter((link) => (options.validate ? options.validate(link.value) : true))
        .at(0);
    return options.editor
        .chain()
        .focus()
        .command(({ tr }) => {
        var _a;
        tr.deleteRange(nodePos, nodePos + ((_a = ((link === null || link === void 0 ? void 0 : link.innerText) || "")) === null || _a === void 0 ? void 0 : _a.length)).replaceWith(nodePos, nodePos, options.editor.schema.text(text, [
            options.editor.schema.marks.hyperlink.create({
                href: sanitizeURL === null || sanitizeURL === void 0 ? void 0 : sanitizeURL.href,
            }),
        ]));
        return true;
    })
        .run();
}

const Hyperlink = Mark.create({
    name: "hyperlink",
    priority: 1000,
    keepOnSplit: false,
    onCreate() {
        this.options.protocols.forEach((protocol) => {
            if (typeof protocol === "string") {
                registerCustomProtocol(protocol);
                return;
            }
            registerCustomProtocol(protocol.scheme, protocol.optionalSlashes);
        });
    },
    onDestroy() {
        reset();
    },
    inclusive() {
        return this.options.autoHyperlink;
    },
    addOptions() {
        return {
            openOnClick: true,
            hyperlinkOnPaste: true,
            autoHyperlink: true,
            protocols: [],
            HTMLAttributes: {
                target: "_blank",
                rel: "noopener noreferrer nofollow",
                class: null,
            },
            modals: {
                previewHyperlink: null,
                setHyperlink: null,
            },
            validate: undefined,
        };
    },
    addAttributes() {
        return {
            href: {
                default: null,
            },
            target: {
                default: this.options.HTMLAttributes.target,
            },
            class: {
                default: this.options.HTMLAttributes.class,
            },
        };
    },
    parseHTML() {
        return [{ tag: 'a[href]:not([href *= "javascript:" i])' }];
    },
    renderHTML({ HTMLAttributes }) {
        return ["a", mergeAttributes(this.options.HTMLAttributes, HTMLAttributes), 0];
    },
    addCommands() {
        return {
            setHyperlink: (attributes) => ({ editor, chain }) => {
                if (!this.options.modals.setHyperlink) {
                    return chain()
                        .setMark(this.name, attributes)
                        .setMeta("preventAutohyperlink", true)
                        .run();
                }
                else {
                    this.options.modals.setHyperlink({
                        editor,
                        validate: this.options.validate,
                        extentionName: this.name,
                        attributes,
                        Tooltip,
                        roundArrow,
                    });
                    return true;
                }
            },
            editHyperlink: (attributes) => ({ editor }) => {
                return editHyperlink({
                    ...attributes,
                    editor,
                    validate: this.options.validate,
                });
            },
            editHyperLinkText: (newText) => ({ editor }) => {
                return editHyperlink({
                    editor,
                    newText,
                    validate: this.options.validate,
                });
            },
            editHyperLinkHref: (newURL) => ({ editor }) => {
                return editHyperlink({
                    editor,
                    validate: this.options.validate,
                    newURL,
                });
            },
            unsetHyperlink: () => ({ chain }) => {
                return chain()
                    .unsetMark(this.name, { extendEmptyMarkRange: true })
                    .setMeta("preventAutohyperlink", true)
                    .run();
            },
        };
    },
    addPasteRules() {
        return [
            markPasteRule({
                find: (text) => find(text)
                    .filter((link) => {
                    if (this.options.validate) {
                        return this.options.validate(link.value);
                    }
                    return true;
                })
                    .filter((link) => link.isLink)
                    .map((link) => ({
                    text: link.value,
                    index: link.start,
                    data: link,
                })),
                type: this.type,
                getAttributes: (match) => {
                    var _a;
                    return ({
                        href: (_a = match.data) === null || _a === void 0 ? void 0 : _a.href,
                    });
                },
            }),
        ];
    },
    addProseMirrorPlugins() {
        const plugins = [];
        if (this.options.autoHyperlink) {
            plugins.push(autoHyperlink({
                type: this.type,
                validate: this.options.validate,
            }));
        }
        if (this.options.openOnClick) {
            plugins.push(clickHandler({
                type: this.type,
                editor: this.editor,
                validate: this.options.validate,
                modal: this.options.modals.previewHyperlink,
            }));
        }
        if (this.options.hyperlinkOnPaste) {
            plugins.push(pasteHandler({
                editor: this.editor,
                type: this.type,
            }));
        }
        return plugins;
    },
});

const editHyperlinkHandler = (options) => {
    var _a;
    // Create the form
    const form = document.createElement("form");
    const buttonsWrapper = document.createElement("div");
    const inputsWrapper = document.createElement("div");
    const linkTextInput = document.createElement("input");
    const hrefInput = document.createElement("input");
    const applyButton = document.createElement("button");
    buttonsWrapper.classList.add("hyperlink-edit-modal__buttons-wrapper");
    inputsWrapper.classList.add("hyperlink-edit-modal__inputs-wrapper");
    linkTextInput.type = "text";
    linkTextInput.value = ((_a = options.link) === null || _a === void 0 ? void 0 : _a.innerText) || "";
    linkTextInput.placeholder = "Enter link text";
    hrefInput.type = "text";
    hrefInput.value = options.link.href;
    hrefInput.placeholder = "Enter href";
    applyButton.type = "submit";
    applyButton.classList.add("hyperlink-edit-modal__apply-button");
    applyButton.innerText = "Apply";
    buttonsWrapper.append(applyButton);
    inputsWrapper.append(linkTextInput, hrefInput);
    // Append to form
    form.append(inputsWrapper, buttonsWrapper);
    options.hyperlinkModal.innerHTML = "";
    options.hyperlinkModal.appendChild(form);
    hrefInput.addEventListener("keydown", () => {
        hrefInput.style.outlineColor = " #dadce0";
    });
    hrefInput.addEventListener("keydown", () => {
        linkTextInput.style.outlineColor = " #dadce0";
    });
    // Handle form submission
    form.addEventListener("submit", (e) => {
        e.preventDefault();
        const newLinkText = linkTextInput.value;
        const newHref = hrefInput.value;
        if (!newHref || !newLinkText) {
            if (!newHref)
                hrefInput.style.outlineColor = "red";
            if (!newLinkText)
                linkTextInput.style.outlineColor = "red";
            return;
        }
        const sanitizeURL = find(newHref)
            .filter((link) => link.isLink)
            .filter((link) => {
            if (options.validate) {
                return options.validate(link.value);
            }
            return true;
        })
            .at(0);
        if (!(sanitizeURL === null || sanitizeURL === void 0 ? void 0 : sanitizeURL.href)) {
            hrefInput.style.outlineColor = "red";
            return;
        }
        options.editor.chain().focus().extendMarkRange("hyperlink").editHyperlink({
            newURL: newHref,
            newText: newLinkText,
        });
        options.tippy.hide();
    });
    // Show tooltip
    options.tippy.update(options.editor.view, {
        arrow: roundArrow,
    });
    setTimeout(() => linkTextInput.focus());
};

// prettier-ignore
const Copy = ({ size = 24, fill = 'rgb(104, 81, 255)' } = {}) => {
    return `
        <svg fill=${fill} viewBox="0 0 24 24" width=${size} xmlns="http://www.w3.org/2000/svg">
            <path d="m13 20a5.006 5.006 0 0 0 5-5v-8.757a3.972 3.972 0 0 0 -1.172-2.829l-2.242-2.242a3.972 3.972 0 0 0 -2.829-1.172h-4.757a5.006 5.006 0 0 0 -5 5v10a5.006 5.006 0 0 0 5 5zm-9-5v-10a3 3 0 0 1 3-3s4.919.014 5 .024v1.976a2 2 0 0 0 2 2h1.976c.01.081.024 9 .024 9a3 3 0 0 1 -3 3h-6a3 3 0 0 1 -3-3zm18-7v11a5.006 5.006 0 0 1 -5 5h-9a1 1 0 0 1 0-2h9a3 3 0 0 0 3-3v-11a1 1 0 0 1 2 0z"/>
        </svg>
    `;
};
const LinkSlash = ({ size = 24, fill = "rgb(104, 81, 255)", } = {}) => {
    return `
        <svg fill=${fill} viewBox="0 0 24 24" width=${size} xmlns="http://www.w3.org/2000/svg">
            <path d="M11.953,17.993c-1.557-.074-3.021-.722-4.121-1.823-.23-.23-.446-.482-.641-.748-.325-.446-.227-1.072,.22-1.397,.446-.325,1.071-.227,1.397,.219,.129,.178,.274,.349,.437,.511,.749,.75,1.744,1.19,2.802,1.24,.552,.026,.979,.495,.952,1.046-.025,.536-.468,.953-.998,.953h-.048ZM1.831,13.331C.65,14.511,0,16.081,0,17.75c0,1.669,.65,3.239,1.831,4.419,1.18,1.181,2.749,1.831,4.419,1.831s3.239-.65,4.419-1.831l1.058-1.058c.391-.391,.391-1.023,0-1.414s-1.023-.391-1.414,0l-1.058,1.058c-.803,.803-1.87,1.245-3.005,1.245s-2.202-.442-3.005-1.245-1.245-1.87-1.245-3.005,.442-2.203,1.245-3.005l3.037-3.038c.391-.391,.391-1.024,0-1.414-.391-.391-1.023-.391-1.414,0l-3.037,3.038ZM.293,1.707L22.293,23.707c.195,.195,.451,.293,.707,.293s.512-.098,.707-.293c.391-.391,.391-1.023,0-1.414l-6.581-6.581,5.043-5.043c1.181-1.18,1.831-2.75,1.831-4.419,0-1.669-.65-3.239-1.831-4.419C20.989,.65,19.42,0,17.75,0s-3.239,.65-4.419,1.831l-1.058,1.058c-.391,.391-.391,1.023,0,1.414s1.023,.391,1.414,0l1.058-1.058c.803-.803,1.87-1.245,3.005-1.245s2.202,.442,3.005,1.245,1.245,1.87,1.245,3.005-.442,2.203-1.245,3.005l-5.043,5.043-5.852-5.852c1.612-.808,3.575-.52,4.894,.798,.162,.162,.308,.333,.437,.511,.196,.269,.501,.411,.81,.411,.204,0,.41-.063,.588-.192,.446-.325,.545-.951,.22-1.397-.194-.266-.41-.518-.641-.748-2.095-2.095-5.297-2.408-7.76-.835L1.707,.293c-.391-.391-1.023-.391-1.414,0s-.391,1.023,0,1.414Z"/>
        </svg>
    `;
};
const Pencil = ({ size = 24, fill = "rgb(104, 81, 255)", } = {}) => {
    return `
        <svg fill=${fill} viewBox="0 0 24 24" width=${size} xmlns="http://www.w3.org/2000/svg">
            <path d="M22.853,1.148a3.626,3.626,0,0,0-5.124,0L1.465,17.412A4.968,4.968,0,0,0,0,20.947V23a1,1,0,0,0,1,1H3.053a4.966,4.966,0,0,0,3.535-1.464L22.853,6.271A3.626,3.626,0,0,0,22.853,1.148ZM5.174,21.122A3.022,3.022,0,0,1,3.053,22H2V20.947a2.98,2.98,0,0,1,.879-2.121L15.222,6.483l2.3,2.3ZM21.438,4.857,18.932,7.364l-2.3-2.295,2.507-2.507a1.623,1.623,0,1,1,2.295,2.3Z"/>
        </svg>
    `;
};

function previewHyperlinkModal(options) {
    const href = options.link.href;
    const hyperlinkModal = document.createElement("div");
    const removeButton = document.createElement("button");
    const copyButton = document.createElement("button");
    const editButton = document.createElement("button");
    const newBubble = document.createElement("div");
    newBubble.classList.add("hyperlink-preview-modal__metadata");
    const hrefTitle = document.createElement("a");
    hrefTitle.setAttribute("target", "_blank");
    hrefTitle.setAttribute("rel", "noreferrer");
    hrefTitle.setAttribute("href", href);
    hrefTitle.innerText = href;
    newBubble.append(hrefTitle);
    fetch("/api/metadata", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ url: href }),
    })
        .then((response) => response.json())
        .then((data) => {
        // Create a new bubble with the title
        hrefTitle.setAttribute("href", href);
        hrefTitle.innerText = data.title || data["og:title"] || href;
        newBubble.replaceChildren(hrefTitle);
        // Create an image element if image exists in metadata
        if (data.icon || data.image || data["og:image"]) {
            const img = document.createElement("img");
            img.src = data.icon || data.image || data["og:image"];
            img.alt = data.title || data["og:title"] || "hyperlink image";
            newBubble.appendChild(img);
        }
    })
        .catch((error) => {
        console.error("Error fetching metadata:", error);
    });
    hyperlinkModal.classList.add("hyperlink-preview-modal");
    removeButton.classList.add("hyperlink-preview-modal__remove-button");
    removeButton.innerHTML = LinkSlash();
    editButton.classList.add("hyperlink-preview-modal__edit-button");
    editButton.innerHTML = Pencil();
    copyButton.classList.add("hyperlink-preview-modal__copy-button");
    copyButton.innerHTML = Copy();
    removeButton.addEventListener("click", () => {
        options.tippy.hide();
        return options.editor.chain().focus().unsetHyperlink().run();
    });
    editButton.addEventListener("click", () => editHyperlinkHandler({ ...options, hyperlinkModal }));
    copyButton.addEventListener("click", () => {
        options.tippy.hide();
        navigator.clipboard.writeText(href);
    });
    hyperlinkModal.append(newBubble, copyButton, editButton, removeButton);
    return hyperlinkModal;
}

let tooltip = undefined;
function setHyperlinkModal(options) {
    // Create the tooltip instance
    if (!tooltip)
        tooltip = new options.Tooltip(options);
    // Initialize the tooltip
    let { tippyModal } = tooltip.init();
    const hyperlinkModal = document.createElement("div");
    const buttonsWrapper = document.createElement("div");
    const inputsWrapper = document.createElement("div");
    hyperlinkModal.classList.add("hyperlink-set-modal");
    buttonsWrapper.classList.add("hyperlink-set-modal__buttons-wrapper");
    inputsWrapper.classList.add("hyperlink-set-modal__inputs-wrapper");
    // create a form that contain url input and a button for submit
    const form = document.createElement("form");
    const input = document.createElement("input");
    const button = document.createElement("button");
    input.setAttribute("type", "text");
    input.setAttribute("placeholder", "https://example.com");
    button.setAttribute("type", "submit");
    button.innerText = "Submit";
    inputsWrapper.append(input);
    buttonsWrapper.append(button);
    form.append(inputsWrapper, buttonsWrapper);
    hyperlinkModal.append(form);
    tippyModal.innerHTML = "";
    tippyModal.append(hyperlinkModal);
    tooltip.update(options.editor.view, {
        arrow: options.roundArrow,
    });
    // make sure
    setTimeout(() => {
        input.focus();
        input.style.outlineColor = " #dadce0";
    }, 100);
    input.addEventListener("keydown", () => {
        input.style.outlineColor = " #dadce0";
    });
    // event listenr for submit button
    form.addEventListener("submit", (event) => {
        event.preventDefault();
        const url = input.value;
        if (!url) {
            input.style.outlineColor = "red";
            return;
        }
        const sanitizeURL = find(url)
            .filter((link) => link.isLink)
            .filter((link) => {
            if (options.validate) {
                return options.validate(link.value);
            }
            return true;
        })
            .at(0);
        if (!(sanitizeURL === null || sanitizeURL === void 0 ? void 0 : sanitizeURL.href)) {
            input.style.outlineColor = "red";
            return;
        }
        tooltip === null || tooltip === void 0 ? void 0 : tooltip.hide();
        return options.editor
            .chain()
            .setMark(options.extentionName, { href: sanitizeURL.href })
            .setMeta("preventautohyperlink", true)
            .run();
    });
}

export { Hyperlink, Tooltip, previewHyperlinkModal, setHyperlinkModal };
//# sourceMappingURL=index.js.map
